import { useTranslation } from 'react-i18next'
import * as DialogPrimitive from '@radix-ui/react-dialog'
import { ChevronLeft } from 'lucide-react'
import { cn } from '@/lib/utils.ts'
import NavigationGroups from '@/components/common/menu/NavigationGroups.tsx'
import useNavData from '@/components/common/menu/lib/hooks/useNavData.tsx'
import { useMenuStore } from '@/store/useMenuStore.ts'
import { NavigationDataT, NavigationGroupT } from '@/lib/types/navigationTypes.ts'

/*
Floating bottom sheet for the "Więcej" (main menu) tab. Same visual shell
as BottomTabBarIssuesSheet — Linear-style rounded card anchored above the
tab bar, drag-handle pill on top, subtle dim overlay.

Hosts BOTH layers of the mobile menu (primary + secondary) via drill-down:

  - Tap "Więcej"          → sheet opens on primary (Podsumowanie, Raporty,
                            Kolejka, Ustawienia, Wyloguj)
  - Tap "Ustawienia"      → store flips to secondaryMobile, sheet content
                            swaps to settings. A back-header appears at the
                            top of the sheet ("< Ustawienia") so the user
                            knows they drilled in.
  - Tap back-header       → closeSecondaryMenu → back to primary, sheet
                            stays open.
  - Dismiss (overlay/esc) → both flags cleared, sheet closes.

Previously we tried a separate full-screen settings drawer, but the mode
switch felt disconnected from the bottom-tab entry point. Keeping settings
inside the same sheet — with a clear drill-in affordance — is a better
fit for "the More tab IS the menu".

The secondaryMenuData ships with its own `backToApp` group at the top
(ChevronLeft + "Powrót do aplikacji"), but that row closes everything AND
navigates. Inside the sheet we want back = "go up one level", not "leave
the menu". So we hide the backToApp group in this context and render our
own header button wired to closeSecondaryMenu.
*/

export default function BottomTabBarMoreSheet() {
  const { primaryMenuData, secondaryMenuData } = useNavData()
  const { t } = useTranslation()
  const mobileMenuState = useMenuStore(s => s.mobileMenuState)
  const closeMobileMenu = useMenuStore(s => s.closeMobileMenu)
  const closeSecondaryMenu = useMenuStore(s => s.closeSecondaryMenu)

  const isOpen = mobileMenuState !== 'closedMobile'
  const isSecondary = mobileMenuState === 'secondaryMobile'

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      // Reset both flags so the sheet reopens on the primary layer next time
      // rather than popping back into the last-viewed secondary page.
      closeSecondaryMenu()
      closeMobileMenu()
    }
  }

  // Mobile-only transforms on the primary menu data:
  //
  //  - summary: drop "Skrzynka" (inbox)         — already the Inbox tab
  //  - general: drop "Lista zgłoszeń" (archive) — already the "Wszystkie"
  //    row inside the Zgłoszenia bottom sheet
  //  - issues: drop the whole per-issue-type group — already rendered as
  //    "Wg typu" rows inside the Zgłoszenia bottom sheet
  //  - settings: convert the "Ustawienia" link-item into a function-item
  //    that only drills into the secondary layer. Tapping Ustawienia
  //    should NOT navigate under the sheet — the route change must happen
  //    only when the user picks a specific settings page inside the
  //    secondary layer. Otherwise the primary drill-in double-dips as a
  //    navigation and the background context flickers unnecessarily.
  //
  // Desktop sidebar still consumes primaryMenuData directly and is unaffected.
  const mobilePrimary: NavigationDataT = Object.fromEntries(
    Object.entries(primaryMenuData)
      .filter(([key]) => key !== 'issues')
      // Explicit return-tuple annotation: without it, TS widens the
      // returned `[key, group]` pairs to `(string | NavigationGroupT)[]`
      // (multi-branch returns lose tuple length), which then breaks the
      // `.filter(([, group]) => group.items.length > 0)` step below
      // because `group` could be a string. Annotating as a 2-tuple keeps
      // the inference tight.
      .map(([key, group]): [string, NavigationGroupT] => {
        if (key === 'summary') {
          return [
            key,
            { ...group, items: group.items.filter(it => !it.link?.endsWith('/notifications')) },
          ]
        }
        if (key === 'general') {
          return [
            key,
            { ...group, items: group.items.filter(it => !it.link?.includes('/archive')) },
          ]
        }
        if (key === 'settings') {
          return [
            key,
            {
              ...group,
              // Ustawienia → drill-in button (no route change yet).
              // Wyloguj się → handled by the logout icon button next to
              //   the user identity in the footer, not here.
              items: group.items
                .filter(it => it.function !== 'logout')
                .map(it =>
                  it.link?.startsWith('/settings')
                    ? {
                        name: it.name,
                        icon: it.icon,
                        function: 'openSecondaryMenu' as const,
                        showArrowRight: true,
                      }
                    : it,
                ),
            },
          ]
        }
        return [key, group]
      })
      .filter(([, group]) => group.items.length > 0),
  )

  // Secondary inside the sheet: hide backToApp group (we render our own
  // back header at the top of the sheet, scoped to "go up one level").
  const sheetSecondary: NavigationDataT | null = secondaryMenuData
    ? Object.fromEntries(Object.entries(secondaryMenuData).filter(([key]) => key !== 'backToApp'))
    : null

  const navigationData = isSecondary && sheetSecondary ? sheetSecondary : mobilePrimary
  const menuLayer = isSecondary ? 'secondaryMobile' : 'primaryMobile'

  return (
    <DialogPrimitive.Root open={isOpen} onOpenChange={handleOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={`fixed inset-0 z-[1040] bg-black/25 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0`}
        />
        <DialogPrimitive.Content
          data-cy="bottom-tab-more-sheet"
          onOpenAutoFocus={e => e.preventDefault()}
          className={cn(
            `fixed inset-x-3 z-[1050] mx-auto flex max-w-md flex-col overflow-hidden rounded-2xl border bg-background shadow-2xl`,
            `bottom-[calc(var(--bottomTabBarHeight)+var(--safe-area-inset-bottom)+0.75rem)]`,
            `max-h-[78svh]`,
            `data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:slide-out-to-bottom data-[state=open]:slide-in-from-bottom`,
          )}
        >
          {/* Drag handle — visual affordance, matches the Zgłoszenia sheet.
              Only shown at the primary layer; drilled-in view uses the back
              header as its dominant top element instead. */}
          {!isSecondary && (
            <div className={`flex justify-center pt-2`}>
              <div className={`h-1 w-10 rounded-full bg-muted-foreground/30`} />
            </div>
          )}

          {/* Back header — explicit drill-up affordance. `closeSecondaryMenu`
              flips the store back to primaryMobile, so the sheet stays open
              and swaps content rather than dismissing. */}
          {isSecondary && (
            <button
              type="button"
              onClick={closeSecondaryMenu}
              data-cy="bottom-tab-more-sheet-back"
              className={`flex items-center gap-2 border-b px-3 py-3 text-left hover:bg-accent`}
            >
              <ChevronLeft className={`size-4 text-muted-foreground`} />
              <span className={`text-sm font-semibold`}>{t('navigation.project.settings')}</span>
            </button>
          )}

          <DialogPrimitive.Title className={`sr-only`}>
            {isSecondary ? t('navigation.project.settings') : t('navigation.menu')}
          </DialogPrimitive.Title>

          {/* NavigationGroups owns its own scrollable layout + sticky footer.
              The sheet provides the height cap via max-h; overflow lives on
              the NavigationGroups container below. */}
          <div className={`flex flex-1 flex-col overflow-y-auto`}>
            <NavigationGroups navigationData={navigationData} menuLayer={menuLayer} />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}
